import type { RGBToHexOptions } from './types/rgb-to-hex.options';
import type { RGB } from './types/rgb.type';

import { parseRgbaString } from './parse-rbga-string';

/**
 * Converts an RGB or RGBA color value to its hexadecimal string representation.
 *
 * Accepts either an `RGB` object or a CSS color string (e.g., `'rgba(255, 0, 0, 0.5)'`).
 * Supports options to add a hash prefix and allow shorthand hex notation.
 *
 * @param value - The color value to convert, either as an `RGB` object or a CSS color string.
 * @param options - Conversion options:
 *   - `addHash` (default: `true`): Whether to prefix the result with a `#`.
 *   - `allowShorthand` (default: `true`): Whether to use shorthand hex notation when possible.
 * @returns The hexadecimal color string, optionally prefixed with `#` and in shorthand notation if allowed.
 *
 * @throws {TypeError} If the input value is not a valid color.
 *
 * @example
 * ```typescript
 * rgbToHex({ red: 255, green: 0, blue: 0 }); // '#ff0000ff'
 * rgbToHex('rgba(255, 0, 0, 0.5)', { addHash: false }); // 'ff000080'
 * rgbToHex({ red: 255, green: 255, blue: 255 }, { allowShorthand: true }); // '#fff'
 * ```
 */
export const rgbToHex = (value: RGB | string, options: RGBToHexOptions = {}) => {
  const { addHash = true, allowShorthand = false } = options;
  let a: number;
  let b: number;
  let g: number;
  let r: number;

  if (typeof value === 'string') {
    ({ alpha: a = 1, blue: b, green: g, red: r } = parseRgbaString(value));
  } else {
    ({ alpha: a = 1, blue: b, green: g, red: r } = value);
  }

  const toHex = (rgbComponent: number) => {
    return Math.round(rgbComponent).toString(16).padStart(2, '0');
  };

  const alphaHex = toHex(Math.round(a * 255));
  let hex = `${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;

  if (allowShorthand) {
    const match = /^([0-9a-f])\1([0-9a-f])\2([0-9a-f])\3([0-9a-f])\4?$/i.exec(hex);

    if (match !== null) {
      hex = `${match[1]}${match[2]}${match[3]}${match[4] ?? ''}`;
    }
  }

  return (addHash ? `#${hex}` : hex).toLowerCase();
};
